<template>
  <ModalComponent v-model:dialogVisible="showModal">
    <template #modalContent>
      <CropperComponent
        :imageUrl="imageUrl"
        :aspect-ratio="aspectRatio"
        :view-mode="viewMode"
        @cropped="handleCroppedImage"
        @cancel="handleCancel"
        @close-dialog="handleCloseDialog"
      />
    </template>
  </ModalComponent>
  <div
    :key="refreshKey"
    class="flex flex-col items-center w-full gap-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl shadow-blue-gray-900/5 bg-clip-border"
  >
    <div
      class="relative flex flex-col w-full font-regular p-8 portrait:px-2 portrait:py-5 py-10 text-gray-900 dark:text-gray-100 max-w-5xl"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5 mb-6 items-center portrait:items-start"
      >
        <div
          class="w-full max-w-fit flex flex-row gap-5 items-start justify-start portrait:items-start landscape:mr-2"
        >
          <button
            @click="goback"
            class="px-4 flex flex-col items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 border-blue-gray-600/10 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 ease-in-out transform hover:scale-105"
            aria-label="Go back"
          >
            <i class="fa-solid fa-arrow-left text-[16px]"></i>
          </button>
          <div>
            <h3
              class="block text-xl antialiased font-medium leading-snug tracking-normal dark:text-gray-200"
            >
              Product Settings
            </h3>
            <p
              class="text-[14px] font-normal leading-6 text-gray-800 dark:text-gray-300 opacity-80"
            >
              Add features to help make your product sell out faster.
            </p>
          </div>
        </div>
        <a
          href="#"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-4 bg-blue-gray-500/10 rounded-lg text-[14px] border-blue-gray-600/10 font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:border-gray-600 dark:text-gray-200 transition-all duration-300 ease-in-out transform hover:scale-105 hover:shadow-lg hover:bg-blue-gray-500/30"
        >
          <i
            class="fa-regular fa-circle-play dark:text-gray-200 transition-transform duration-300 ease-in-out transform hover:rotate-12"
          ></i>
          <span>Watch Video Tutorial</span>
        </a>
      </nav>
      <div
        class="w-full border-[1px] bg-white dark:bg-gray-700 border-gray-900/10 dark:border-gray-700 flex flex-col h-full rounded-lg relative"
      >
        <h4
          class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex rounded-t-lg text-[18px] font-medium"
        >
          Product Category
        </h4>
        <form class="flex flex-col gap-4 mt-4 py-6">
          <div class="mb-4 px-6">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Add product category
            </p>
            <div class="relative h-14 w-full">
              <!-- Input Field -->
              <input
                type="text"
                v-model="categoryValue"
                placeholder="bag"
                class="peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-500 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-500 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-[85px]"
                :class="{
                  '!outline-red-800 !bg-red-800/10 !border-red-800 !text-red-600 focus:!border-t-red-800 !border-t-red-800 placeholder-red-800/60':
                    categoryError,
                }"
              />

              <!-- Add Category Button -->
              <div class="absolute inset-y-0 right-0 p-1">
                <button
                  @click="addCategory"
                  type="button"
                  class="bg-blue-gray-50 dark:bg-gray-800 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-300 flex gap-1 items-center justify-center transition-all"
                >
                  <i class="fa-solid fa-plus text-sm"></i> Add Category
                </button>
              </div>
            </div>
          </div>
          <div class="w-full flex flex-wrap px-6 gap-2">
            <span
              v-for="(category, index) in productCategories"
              :key="index"
              class="relative flex items-center max-w-[150px] bg-blue-gray-50 dark:bg-gray-600 rounded-lg py-2 pl-2 pr-8 text-sm text-blue-gray-800 dark:text-gray-200 transition-colors"
            >
              <span class="flex-1 truncate text-left" title="{{ category }}">
                {{ category }}
              </span>

              <button
                @click="deleteCategory(index)"
                type="button"
                class="absolute right-1 p-2 h-6 w-6 flex items-center justify-center hover:opacity-75 transition-opacity text-main-900 hover:text-main-600 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Delete category"
              >
                <i class="fa-solid fa-trash text-[14px]"></i>
              </button>
            </span>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Size
          </h4>
          <div class="my-4 px-6">
            <p
              class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Add product size
            </p>
            <div class="relative h-14 w-full">
              <div class="p-1 absolute right-0 inset-y-0">
                <button
                  @click="addSize"
                  type="button"
                  class="bg-blue-gray-50 dark:bg-gray-800 rounded-r-lg px-4 py-3 h-full hover:bg-blue-gray-100 dark:hover:bg-gray-600 text-blue-gray-800 dark:text-gray-300 flex gap-1 items-center justify-center transition-all"
                >
                  <i class="fa-solid fa-plus"></i>Add Size
                </button>
              </div>
              <input
                type="text"
                placeholder="XL"
                v-model="sizeValue"
                :class="{
                  'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline-none transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-500 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-500 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-[85px]': true,
                  'outline-2 !outline-red-800 !bg-red-800/10 !border-red-800 !text-red-600 focus:!border-t-red-800 !border-t-red-800 placeholder-red-800/60':
                    sizeError,
                }"
              />
            </div>
          </div>
          <div class="w-full flex flex-wrap px-6 gap-1">
            <span
              v-for="(size, index) in productSizes"
              :key="index"
              class="max-w-[150px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 pr-8 pl-2 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-gray-50 text-blue-gray-800 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-600/50"
            >
              {{ size }}
              <button
                @click="deleteSize(index)"
                type="button"
                class="!absolute p-2 h-6 w-6 right-1 flex items-center justify-center hover:opacity-75 transition-opacity"
                :class="{
                  'text-main-900 hover:text-main-600': true,
                  'dark:text-gray-400 dark:hover:text-gray-200': true,
                }"
                aria-label="Delete category"
              >
                <i class="fa-solid fa-trash text-[14px]"></i>
              </button>
            </span>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Collections
          </h4>
          <div class="mt-4 px-6 flex flex-col gap-4">
            <p
              class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
            >
              Add product collection
            </p>
            <div class="relative w-full">
              <div
                class="w-full flex flex-col items-start justify-center pt-6 p-2 rounded-lg border border-blue-gray-50 gap-3"
              >
                <div class="mb-4 px-4 w-full">
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Collection Name
                  </p>
                  <div class="relative h-14 w-full">
                    <input
                      type="text"
                      v-model="collectionName"
                      placeholder="Collection 1"
                      :class="{
                        'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                        'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                          collectionNameError,
                      }"
                    />
                  </div>
                </div>
                <div class="mb-4 px-4 w-full">
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Collection Description
                  </p>
                  <div class="relative w-full">
                    <textarea
                      placeholder="We sell a lot of things"
                      v-model="collectionDescription"
                      :class="{
                        'peer h-full w-full min-h-28 rounded-lg border border-blue-gray-200 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-800 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-gray-900 focus:border-t-transparent focus:!border-t-gray-900 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50': true, // Light mode styles
                        'dark:border-gray-600 dark:!border-t-gray-600 dark:text-gray-200 dark:placeholder-shown:border-t-gray-600 dark:focus:border-gray-500 dark:focus:!border-t-gray-500 dark:disabled:bg-gray-700': true, // Dark mode styles
                        'outline-2 !outline-red-800 !bg-red-800/10 !border-red-800 !text-red-600 focus:!border-t-red-800 !border-t-red-800 placeholder-red-800/60':
                          collectionDescriptionError, // Error state styles
                      }"
                    ></textarea>
                  </div>
                </div>
                <div class="mb-4 px-4 w-full">
                  <p
                    class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Add Products
                  </p>
                  <div class="relative h-14 w-full">
                    <div
                      class="flex items-center justify-between h-14 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 font-normal rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-300"
                      @click="toggleCollectionProducts"
                    >
                      <span
                        class="sBtn-text text-gray-700 dark:text-gray-200 truncate"
                      >
                        {{
                          collectionProducts.length > 0
                            ? collectionProducts
                                .map((product) => product.name)
                                .join(", ")
                            : "Select Product"
                        }}
                      </span>
                      <i
                        :class="{
                          'transform rotate-180': isActive,
                          'fa-solid fa-chevron-down': true,
                        }"
                        class="transition-transform duration-300 text-gray-600 dark:text-gray-300"
                      ></i>
                    </div>
                    <ul
                      v-if="isActive"
                      class="absolute z-10 w-full mt-2 max-h-72 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg text-gray-700 dark:text-gray-200"
                    >
                      <li
                        v-if="!isActive"
                        class="text-center text-sm text-gray-500 dark:text-gray-400 p-3"
                      >
                        No Product available.
                      </li>
                      <li
                        v-for="option in visibleProducts"
                        :key="option.id"
                        class="flex items-center h-14 px-4 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300"
                      >
                        <input
                          type="checkbox"
                          :id="option.id"
                          :value="option"
                          v-model="collectionProducts"
                          class="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400 transition duration-150 ease-in-out"
                        />
                        <label
                          :for="option.id"
                          class="ml-2 flex items-center cursor-pointer"
                        >
                          <span
                            class="option-text text-sm sm:text-base text-gray-800 dark:text-gray-200"
                            >{{ option.name }}</span
                          >
                        </label>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="mb-4 px-4 w-full">
                  <div
                    class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
                  >
                    <div class="mb-4 flex flex-col items-start m-0 p-0">
                      <p
                        class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                      >
                        Collection Banner
                      </p>
                      <p class="text-xs">
                        <i
                          class="fa-solid fa-circle-exclamation dark:text-sub-900 text-main-800"
                        ></i>
                        Max file size: 5mb
                      </p>
                    </div>
                    <div class="relative w-full h-auto">
                      <div class="flex flex-col gap-1 mb-2">
                        <p
                          v-if="exceedImageFileSize"
                          class="text-red-800 text-sm"
                        >
                          Maximum file size:
                          <strong>{{ maxFileSize }} MB</strong>
                        </p>
                        <p v-if="invalidFileType" class="text-red-800 text-sm">
                          Allowed file types: <strong>jpg, png</strong>
                        </p>
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <div
                          v-if="collectionImageUrl"
                          class="w-[80px] aspect-square rounded-lg border-dashed border-main-800 flex flex-col items-center justify-center border relative p-1"
                        >
                          <img
                            :src="getFileUrl(collectionImageUrl)"
                            @error="
                              (event) =>
                                (event.target.src =
                                  '/landscape-placeholder.svg')
                            "
                            class="h-full w-full object-cover object-center rounded-lg"
                          />
                          <button
                            @click="collectionImageUrl = ''"
                            data-dismissible-target="alert"
                            class="!absolute p-2 bg-white/80 h-6 w-6 top-2 right-2 select-none rounded-lg text-center align-middle font-medium uppercase text-gray-800 transition-all hover:bg-gray-800/20 active:bg-gray-800/30 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none flex flex-col justify-center items-center"
                            type="button"
                          >
                            <i
                              class="fa-solid fa-trash text-[16px] text-main-900"
                            ></i>
                          </button>
                        </div>
                        <label
                          v-if="collectionImageUrl === null"
                          class="w-[80px] aspect-square rounded-lg border-dashed border-gray-400 flex flex-col items-center justify-center border cursor-pointer"
                        >
                          <i class="fa-solid fa-plus text-gray-400"></i>
                          <input
                            type="file"
                            accept="image/*"
                            class="hidden"
                            @change="handleCollectionImageUpload"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="totalCollections > 0" class="mb-4 px-4 w-full">
                  <div
                    class="w-full flex flex-col items-center justify-center p-4 rounded-lg border border-blue-gray-50 bg-sub-200"
                  >
                    <p
                      class="text-center text-sm leading-normal text-main-900 font-medium"
                    >
                      {{
                        `You have a total of ${totalCollections} collection(s)`
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex flex-row justify-end items-center w-full px-4 mt-4 mb-1"
                >
                  <button
                    @click="createCollection"
                    class="portrait:w-full align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-3 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-800 dark:hover:shadow-sub-800/40"
                  >
                    Create Collection
                  </button>
                </div>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Discount (%)
          </h4>
          <div class="mb-4 px-6 w-full">
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
            >
              <div class="mb-4 px-4 w-full">
                <div class="mb-4 flex flex-col items-start m-0 p-0">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Discount Products
                  </p>
                  <p class="text-sm">
                    <i
                      class="fa-solid fa-circle-exclamation dark:text-sub-900 text-main-800"
                    ></i>
                    Choose the Products you want the discount to be applied to.
                  </p>
                </div>
                <div class="relative h-14 w-full">
                  <div
                    class="flex items-center justify-between h-14 bg-white dark:bg-gray-700 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 font-normal rounded-lg cursor-pointer border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-300"
                    @click="toggleDiscountProducts"
                  >
                    <span
                      class="sBtn-text text-gray-700 dark:text-gray-200 truncate"
                    >
                      {{
                        discountProducts.length > 0
                          ? discountProducts
                              .map((product) => product.name)
                              .join(", ")
                          : "Select Products"
                      }}
                    </span>
                    <i
                      :class="{
                        'transform rotate-180': discountIsActive,
                        'fa-solid fa-chevron-down': true,
                      }"
                      class="transition-transform duration-300 text-gray-600 dark:text-gray-300"
                    ></i>
                  </div>
                  <ul
                    v-if="discountIsActive"
                    class="absolute z-10 w-full mt-2 max-h-72 overflow-y-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg text-gray-700 dark:text-gray-200"
                  >
                    <template v-if="discountProduct.length > 0"
                      ><li
                        v-for="option in discountProduct"
                        :key="option.id"
                        class="flex items-center h-14 px-4 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-300"
                      >
                        <input
                          type="checkbox"
                          :id="option.id"
                          :value="option"
                          v-model="discountProducts"
                          class="form-checkbox h-5 w-5 text-blue-600 dark:text-blue-400 transition duration-150 ease-in-out"
                        />
                        <label
                          :for="option.id"
                          class="ml-2 flex items-center cursor-pointer"
                        >
                          <span
                            class="option-text text-sm sm:text-base text-gray-800 dark:text-gray-200"
                            >{{ option.name }}</span
                          >
                        </label>
                      </li></template
                    ><template v-else>
                      <li class="text-center text-gray-500">
                        No products available for discount.
                      </li>
                    </template>
                  </ul>
                </div>
              </div>
              <div class="mt-4 mb-1 px-4 w-full">
                <div class="flex flex-col items-start mb-4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Percentage Off
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation dark:text-sub-900 text-main-800"
                    ></i>
                    <p class="text-sm">
                      How many percent off are you giving for each of these
                      products.
                    </p>
                  </div>
                </div>
                <div class="relative w-full h-14">
                  <span
                    class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-200 top-2/4 right-4 font-bold -translate-y-2/4"
                    >%</span
                  >
                  <input
                    type="number"
                    placeholder="7.5"
                    step="0.01"
                    min="0"
                    inputmode="decimal"
                    v-model="discountPercentage"
                    :class="{
                      'no-spinner peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-10': true,
                      'outline-2 !outline-red-800 !bg-red-800/10 !border-red-800 !text-red-600 focus:!border-t-red-800 !border-t-red-800 placeholder-red-800/60':
                        productNameError,
                    }"
                  />
                </div>
              </div>
              <div
                class="flex flex-row justify-end items-center w-full px-4 mt-4 mb-1"
              >
                <button
                  type="button"
                  @click="createDiscount"
                  class="portrait:w-full align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-3 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-800 dark:hover:shadow-sub-800/40"
                >
                  Create Discount
                </button>
              </div>
              <div class="w-full flex flex-wrap px-6 gap-1 py-1">
                <span
                  v-for="(discount, index) in productDiscounts"
                  :key="index"
                  class="max-w-[200px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 pr-8 pl-2 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-gray-50 text-blue-gray-800 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-600/50"
                  ><span
                    class="overflow-hidden text-ellipsis whitespace-nowrap"
                  >
                    {{
                      `(${Number(discount.discountPercent) % 1 === 0 ? Number(discount.discountPercent).toFixed(0) : Number(discount.discountPercent).toFixed(1)}%) - ${discount.productIds?.map((product) => product.name).join(", ") || "No Products"}`
                    }}</span
                  >
                  <button
                    @click="deleteDiscount(index)"
                    type="button"
                    class="!absolute p-2 h-6 w-6 right-1 flex items-center justify-center hover:opacity-75 transition-opacity"
                    :class="{
                      'text-main-900 hover:text-main-600': true,
                      'dark:text-gray-400 dark:hover:text-gray-200': true,
                    }"
                    aria-label="Delete discount"
                  >
                    <i class="fa-solid fa-trash text-[14px]"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
          <h4
            class="text-gray-800 dark:text-gray-200 bg-gray-100 dark:bg-gray-600 p-6 inline-flex text-[18px] font-medium"
          >
            Product Coupon
          </h4>
          <div class="mb-4 px-6 w-full">
            <div
              class="w-full flex flex-col items-start justify-center p-4 rounded-lg border border-blue-gray-50"
            >
              <div class="mb-4 px-4 w-full">
                <p
                  class="block mb-2 antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                >
                  Coupon Code
                </p>
                <div class="relative h-14 w-full">
                  <input
                    type="text"
                    v-model="couponCode"
                    @input="couponCode = couponCode.toUpperCase()"
                    placeholder="ATOZ2025"
                    :class="{
                      'peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        couponNameError,
                    }"
                  />
                </div>
              </div>
              <div class="mt-4 mb-1 px-4 w-full">
                <div class="flex flex-col items-start mb-4 gap-1">
                  <p
                    class="block antialiased font-medium leading-normal text-gray-900 dark:text-gray-200"
                  >
                    Percentage Off
                  </p>
                  <div class="flex flex-row justify-center items-start gap-1">
                    <i
                      class="fa-solid fa-circle-exclamation dark:text-sub-900 text-main-800"
                    ></i>
                    <p class="text-sm">
                      This percentage will be applied to product subtotal at
                      checkout.
                    </p>
                  </div>
                </div>
                <div class="relative w-full h-14">
                  <span
                    class="absolute grid w-5 h-5 place-items-center text-gray-800/80 dark:text-gray-200 top-2/4 right-4 font-bold -translate-y-2/4"
                    >%</span
                  >
                  <input
                    type="number"
                    placeholder="7.5"
                    step="0.01"
                    min="0"
                    inputmode="decimal"
                    v-model="couponPercentage"
                    :class="{
                      'no-spinner peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 pr-10': true,
                      'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
                        couponPercentError,
                    }"
                  />
                </div>
              </div>
              <div
                class="flex flex-row justify-end items-center w-full px-4 mt-4 mb-1"
              >
                <button
                  type="button"
                  @click="createCoupon"
                  class="portrait:w-full align-middle select-none font-semibold text-center transition-all disabled:opacity-50 disabled:shadow-none disabled:pointer-events-none text-base portrait:text-sm py-3 px-4 rounded-lg bg-button text-white shadow-md shadow-gray-900/10 hover:shadow-lg hover:shadow-main-200 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none dark:bg-sub-800 dark:hover:shadow-sub-800/40"
                >
                  Create Coupon
                </button>
              </div>
              <div class="w-full flex flex-wrap px-6 gap-1 py-1">
                <span
                  v-for="(coupon, index) in couponCodes"
                  :key="coupon.id"
                  class="max-w-[200px] flex w-min overflow-hidden text-ellipsis whitespace-nowrap py-2 pr-8 pl-2 rounded-lg text-sm text-center items-center justify-center relative transition-colors bg-blue-gray-50 text-blue-gray-800 dark:bg-gray-600 dark:text-gray-200 dark:hover:bg-gray-600/50"
                >
                  <span
                    class="overflow-hidden text-ellipsis whitespace-nowrap"
                    >{{
                      `${coupon.coupon_code} - (${
                        Number(coupon.coupon_percent) % 1 === 0
                          ? Number(coupon.coupon_percent).toFixed(0)
                          : Number(coupon.coupon_percent).toFixed(1)
                      }%)`
                    }}</span
                  >

                  <button
                    @click="deleteCoupon(index)"
                    type="button"
                    class="!absolute p-2 h-6 w-6 right-1 flex items-center justify-center hover:opacity-75 transition-opacity"
                    :class="{
                      'text-main-900 hover:text-main-600': true,
                      'dark:text-gray-400 dark:hover:text-gray-200': true,
                    }"
                    aria-label="Delete coupon"
                  >
                    <i class="fa-solid fa-trash text-[14px]"></i>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import ModalComponent from "@/components/modalComponent.vue";
import CropperComponent from "@/components/cropperComponent.vue";
import { mapActions } from "vuex";

export default {
  name: "ProductSettings",
  inject: ["getFileUrl"],
  props: {
    maxFileSize: {
      type: Number,
      default: 5,
    },
  },
  components: {
    ModalComponent,
    CropperComponent,
  },
  data() {
    return {
      notificationSettings: {
        position: "bottom-right",
        transition: "slide",
        autoClose: 1000,
        theme: "auto",
        pauseOnHover: true,
        closeOnClick: true,
      },
      categoryValue: "",
      categoryError: false,
      productCategories: [],
      productSizes: [],
      products: [],
      sizeValue: "",
      sizeError: false,
      productNameError: false,
      collectionName: "",
      collectionNameError: false,
      collectionDescription: "",
      collectionDescriptionError: false,
      collectionProducts: [],
      productDiscounts: [],
      isActive: false,
      productOptions: [],
      exceedImageFileSize: false,
      invalidFileType: false,
      collectionImageUrl: null,
      imageUrl: "",
      file: null,
      showModal: false,
      viewMode: 1,
      aspectRatio: 1,
      discountProducts: [],
      discountPercentage: 7.5,
      discountIsActive: false,
      totalCollections: 0,
      discountedProducts: [],
      couponCode: "",
      couponPercentage: 7.5,
      couponNameError: false,
      couponPercentError: false,
      couponCodes: [],
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
    };
  },
  mounted() {
    this.fetchCategories();
    this.fetchSizes();
    this.fetchProducts();
    this.fetchCollections();
    this.fetchDiscounts();
    this.fetchCoupons();
  },
  watch: {
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchCategories();
        this.fetchSizes();
        this.fetchProducts();
        this.fetchCollections();
        this.fetchDiscounts();
        this.fetchCoupons();
      },
    },
  },
  computed: {
    discountProduct() {
      return this.visibleProducts.filter(
        (product) =>
          !this.discountedProducts.some(
            (discountedProduct) => discountedProduct.id === product.id
          )
      );
    },
    visibleProducts() {
      const products = this.products.filter(
        (product) => product.visibility === 1
      );
      return products.map((product) => ({
        id: product.id,
        name: product.name,
      }));
    },
  },
  methods: {
    ...mapActions([
      "getProductCategory",
      "addProductCategory",
      "getProductSize",
      "addProductSize",
      "getProducts",
      "addCollection",
      "getCollection",
      "addDiscount",
      "getDiscount",
      "deleteDiscountDB",
      "addCoupon",
      "getCoupon",
      "deleteCouponDB",
    ]),
    async fetchDiscounts() {
      const response = await this.getDiscount();
      if (response.success) {
        this.discountedProducts = response.allProductId;
        this.productDiscounts = response.discounts;
        console.log(this.productDiscounts, "from database");
      } else {
        console.error(response.error);
      }
    },
    async fetchCollections() {
      const response = await this.getCollection();
      if (response.success) {
        this.totalCollections = response.totalCollections;
      } else {
        console.error(response.error);
      }
    },
    async fetchProducts() {
      const response = await this.getProducts();
      if (response.success) {
        this.products = response.products;
      } else {
        console.error(response.error);
      }
    },
    async fetchCategories() {
      const response = await this.getProductCategory();
      if (response.success) {
        this.productCategories = response.categories;
      } else {
        console.error(response.error);
      }
    },
    async fetchSizes() {
      const response = await this.getProductSize();
      if (response.success) {
        this.productSizes = response.sizes;
      } else {
        console.error(response.error);
      }
    },
    async createCollection(event) {
      event.preventDefault();

      const formData = new FormData();
      formData.append("collectionName", this.collectionName);
      formData.append("collectionDescription", this.collectionDescription);
      const productIds = this.collectionProducts.map((product) => product.id);
      formData.append("products", JSON.stringify(productIds));
      if (this.collectionImageUrl) {
        formData.append("banner", this.collectionImageUrl);
      }

      try {
        const generalSettingsPromise = this.addCollection(formData).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Update Failed!"));
            }
            this.collectionName = "";
            this.collectionDescription = "";
            this.collectionProducts = [];
            this.collectionImageUrl = null;
            this.totalCollections = 0;
            this.totalCollections = data.response;
            return data.response;
          }
        );
        toast.promise(
          generalSettingsPromise,
          {
            pending: "Adding collection...",
            success: {
              render() {
                return "Collection added successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to add collection!";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );
      } catch (error) {
        console.error("There has been an error: ", error);
      }
    },
    goback() {
      this.$router.back();
    },
    toggleCollectionProducts() {
      this.isActive = !this.isActive;
    },
    toggleDiscountProducts() {
      this.discountIsActive = !this.discountIsActive;
    },
    async addCategory() {
      if (this.categoryValue) {
        // Ensure productCategories is an array
        if (!Array.isArray(this.productCategories)) {
          this.productCategories = [];
        }

        // Check if category already exists
        const categoryExists = this.productCategories.includes(
          this.categoryValue.trim()
        );
        if (categoryExists) {
          this.categoryError = true; // Set error flag
          setTimeout(() => {
            this.categoryError = false; // Reset error flag after a delay
          }, 3000); // Keep the error state for 3 seconds
          return;
        }

        if (!Array.isArray(this.productCategories)) {
          this.productCategories = [];
        }

        const updatedCategories = [
          ...this.productCategories,
          this.categoryValue.trim(), // Trim any leading/trailing spaces
        ];

        const categoryPromise = this.addProductCategory(updatedCategories).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Failed to update categories!"));
            }
            return data.response;
          }
        );

        toast.promise(
          categoryPromise,
          {
            pending: "Adding category...",
            success: {
              render() {
                return "Category added successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to add category.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await categoryPromise;
          this.productCategories = updatedCategories;
          this.categoryValue = "";
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },

    async deleteCategory(index) {
      if (this.productCategories.length > 0) {
        const updatedCategories = [...this.productCategories];
        updatedCategories.splice(index, 1);

        const categoryPromise = this.addProductCategory(updatedCategories).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(new Error("Failed to update categories!"));
            }
            return data.response;
          }
        );

        toast.promise(
          categoryPromise,
          {
            pending: "Updating categories...",
            success: {
              render() {
                return "Categories updated successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to update categories.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await categoryPromise;
          this.productCategories = updatedCategories;
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },

    async deleteDiscount(index) {
      if (this.productDiscounts.length > 0) {
        const discountToDelete = this.productDiscounts[index];
        const updatedDiscounts = [...this.productDiscounts];
        updatedDiscounts.splice(index, 1);

        const discountPromise = this.deleteDiscountDB(
          discountToDelete.discountId
        ).then((data) => {
          if (!data.success) {
            return Promise.reject(
              new Error(data.error || "Failed to delete discount!")
            );
          }
          return data.response;
        });

        toast.promise(
          discountPromise,
          {
            pending: "Deleting discount...",
            success: {
              render() {
                return "Discount deleted successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to delete discount.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await discountPromise;

          const deletedProducts = discountToDelete.productIds;
          this.discountedProducts = this.discountedProducts.filter(
            (product) =>
              !deletedProducts.some((deleted) => deleted.id === product.id)
          );

          this.productDiscounts = updatedDiscounts;
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },

    async deleteCoupon(index) {
      if (this.couponCodes.length > 0) {
        const couponToDelete = this.couponCodes[index];

        const couponPromise = this.deleteCouponDB(couponToDelete.id).then(
          (data) => {
            if (!data.success) {
              return Promise.reject(
                new Error(data.error || "Failed to delete coupon!")
              );
            }
            return data.response;
          }
        );

        toast.promise(
          couponPromise,
          {
            pending: "Deleting coupon...",
            success: {
              render() {
                return "Coupon deleted successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to delete coupon.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await couponPromise;
          // Remove the coupon from the list only after successful deletion
          this.couponCodes.splice(index, 1);
        } catch (error) {
          console.error("Error deleting coupon:", error);
        }
      }
    },

    async createCoupon() {
      if (this.couponCode) {
        const coupon = {
          couponCode: this.couponCode,
          couponPercentage: this.couponPercentage,
        };

        const couponPromise = this.addCoupon(coupon).then((data) => {
          if (!data.success) {
            return Promise.reject(
              new Error(data.error || "Failed to create coupon!")
            );
          }
          return data.coupon;
        });

        toast.promise(
          couponPromise,
          {
            pending: "Creating coupon...",
            success: {
              render() {
                return "Coupon created successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to create coupon.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          const newCoupon = await couponPromise;
          // Add the coupon to the list only after successful creation
          this.couponCodes.push(newCoupon);
          // Reset input fields
          this.couponCode = "";
          this.couponPercentage = 7.5;
        } catch (error) {
          console.error("Error creating coupon:", error);
        }
      }
    },

    async fetchCoupons() {
      const couponPromise = this.getCoupon().then((data) => {
        if (!data.success) {
          return Promise.reject(
            new Error(data.error || "Failed to fetch coupons!")
          );
        }
        return data.coupons;
      });

      toast.promise(
        couponPromise,
        {
          error: {
            render() {
              return "Failed to fetch coupons.";
            },
          },
        },
        {
          ...this.notificationSettings,
          autoClose: 3000,
          position: "top-right",
        }
      );

      try {
        const coupons = await couponPromise;
        // Update the coupon list only after fetching is successful
        this.couponCodes = coupons;
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    },

    async createDiscount() {
      if (this.discountProducts) {
        const discount = {
          productIds: this.discountProducts,
          discountPercent: this.discountPercentage,
        };
        this.productDiscounts.push(discount);
        console.log("create discount", this.productDiscounts);
        const discountPromise = this.addDiscount(discount).then((data) => {
          if (!data.success) {
            return Promise.reject(new Error("Update Failed!"));
          }
          return data.response;
        });

        toast.promise(
          discountPromise,
          {
            pending: "Creating discounts...",
            success: {
              render() {
                return "Discounts created successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to create discounts.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await discountPromise;
          // Reset fields if successful
          this.discountedProducts = [
            ...this.discountedProducts,
            ...this.discountProducts,
          ];
          this.discountProducts = [];
          this.discountPercentage = 7.5;
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },

    async addSize() {
      if (this.sizeValue) {
        if (!Array.isArray(this.productSizes)) {
          this.productSizes = [];
        }

        // Check if size already exists
        const sizeExists = this.productSizes.includes(this.sizeValue.trim());
        if (sizeExists) {
          this.sizeError = true; // Set error flag
          setTimeout(() => {
            this.sizeError = false; // Reset error flag after a delay
          }, 3000); // Keep the error state for 3 seconds
          return;
        }

        if (!Array.isArray(this.productSizes)) {
          this.productSizes = [];
        }

        const updatedSizes = [...this.productSizes, this.sizeValue.trim()];

        // Create a promise for adding the size
        const sizePromise = this.addProductSize(updatedSizes).then((data) => {
          if (!data.success) {
            return Promise.reject(new Error("Update Failed!"));
          }
          return data.response;
        });

        // Use toast.promise for notifications
        toast.promise(
          sizePromise,
          {
            pending: "Updating sizes...",
            success: {
              render() {
                return "Sizes updated successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to update sizes.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await sizePromise;
          // Update the size list and reset sizeValue if successful
          this.productSizes = updatedSizes;
          this.sizeValue = "";
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },

    async deleteSize(index) {
      if (this.productSizes.length > 0) {
        const updatedSizes = [...this.productSizes];
        updatedSizes.splice(index, 1);

        const sizePromise = this.addProductSize(updatedSizes).then((data) => {
          if (!data.success) {
            return Promise.reject(new Error("Update Failed!"));
          }
          return data.response;
        });

        toast.promise(
          sizePromise,
          {
            pending: "Deleting size...",
            success: {
              render() {
                return "Size deleted successfully!";
              },
              icon: true,
            },
            error: {
              render() {
                return "Failed to delete size.";
              },
            },
          },
          {
            ...this.notificationSettings,
            autoClose: 3000,
            position: "top-right",
          }
        );

        try {
          await sizePromise;
          this.productSizes = updatedSizes;
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    },
    handleCollectionImageUpload(event) {
      const files = event.target.files;
      this.exceedImageFileSize = false;
      this.invalidFileType = false;

      for (let file of files) {
        const fileType = file.type;
        if (
          fileType !== "image/jpeg" &&
          fileType !== "image/png" &&
          fileType !== "image/jpg"
        ) {
          this.invalidFileType = true;
          return;
        }

        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > this.maxFileSize) {
          this.exceedImageFileSize = true;
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          this.imageUrl = e.target.result;
          this.file = file;
          this.showModal = true;
          event.target.value = "";
        };
        reader.readAsDataURL(file);
      }
    },
    handleCroppedImage(croppedImage) {
      const fileDetails = this.file;
      croppedImage.toBlob((blob) => {
        const croppedFile = new File([blob], fileDetails.name, {
          type: fileDetails.type,
        });
        this.collectionImageUrl = croppedFile;
        this.showModal = false;
      });
    },
    handleCancel() {
      this.imageUrl = "";
      this.file = null;
      this.showModal = false;
    },
    handleCloseDialog() {
      this.collectionImageUrl = this.file;
      this.showModal = false;
    },
  },
};
</script>
<style scoped>
.optionsActive {
  animation-name: fadeInUp;
  -webkit-animation-name: fadeInUp;
  animation-duration: 0.4s;
  animation-fill-mode: both;
  -webkit-animation-duration: 0.4s;
  -webkit-animation-fill-mode: both;
}
</style>
